import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Mapping, Transform } from '@atlaskit/editor-prosemirror/transform';

import type { Suggestion } from '../../aiSuggestionsPluginType';
import { applyToolCalls } from '../../ui/utils/toolCalls/applyToolCalls';

/**
 * The range of `doc` spanning every point the suggestion's tool calls insert content at, which is
 * where the diff renders its widgets — possibly outside the target, e.g. a list item replacing a
 * paragraph lands after the enclosing `<li>` (see `safeInsertAtPos`). Deletions are ignored, as
 * the diff renders them in place.
 */
export function getSuggestionToolCallRange(
	doc: PMNode,
	suggestion: Suggestion,
): { from: number; to: number } | undefined {
	if (!suggestion.toolCalls?.length) {
		return undefined;
	}

	let transform: Transform;
	try {
		transform = applyToolCalls({ tr: new Transform(doc), suggestion });
	} catch {
		return undefined;
	}

	const stepMaps = transform.steps.map((step) => step.getMap());
	let from = Infinity;
	let to = -Infinity;

	stepMaps.forEach((stepMap, index) => {
		// Built explicitly: `Mapping.slice(...).invert()` inverts every map, not just the slice's.
		const toOriginalDoc = new Mapping(
			stepMaps
				.slice(0, index)
				.reverse()
				.map((map) => map.invert()),
		);

		stepMap.forEach((oldStart, oldEnd, newStart, newEnd) => {
			if (newEnd > newStart) {
				from = Math.min(from, toOriginalDoc.map(oldStart, -1));
				to = Math.max(to, toOriginalDoc.map(oldEnd, 1));
			}
		});
	});

	return from <= to ? { from, to } : undefined;
}

/**
 * First position in `doc` the suggestion changes. Uses a doc diff (not step maps) so
 * mark/attr-only suggestions, which don't move positions, still resolve.
 */
export function getSuggestionChangeStart(doc: PMNode, suggestion: Suggestion): number | undefined {
	if (!suggestion.toolCalls?.length) {
		return undefined;
	}

	let transform: Transform | undefined;
	try {
		transform = applyToolCalls({ tr: new Transform(doc), suggestion });
	} catch {
		return undefined;
	}

	return transform?.doc.content.findDiffStart(doc.content) ?? undefined;
}
